import { Button } from "@fido/ui-lib";
import { InteractionStatus, PromptValue } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";

import LogoIcon from "@/assets/icons/logo_blue.svg?react";
import { userAuthorizedSelector } from "@/redux/authorization/authorization.selector";
import { checkEntraAuthGate } from "@/utils/statsig";
import { sanitizeRedirectTo } from "@/loaders/auth.loaders";
import { debug } from "@/utils/debug";
import { scopes } from "@/msal/authConfig";
import useCustomTranslation from "@/localization/useCustomTranslation";

import LoginContainer from "./LoginContainer";

export const LoginEntry = () => {
  const { prefixedT } = useCustomTranslation("LOGIN_PAGE");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { instance, inProgress } = useMsal();
  const isUsingB2CAuth = checkEntraAuthGate();
  const { isAuthorized } = useSelector(userAuthorizedSelector);

  const redirectTo = sanitizeRedirectTo(searchParams.get("redirectTo"));

  useEffect(() => {
    debug.log("[login-entry] state", {
      isUsingB2CAuth,
      isAuthorized,
      inProgress,
      redirectTo: redirectTo ?? "none"
    });
  }, [isUsingB2CAuth, isAuthorized, inProgress, redirectTo]);

  useEffect(() => {
    if (!isAuthorized) return;
    const destination = redirectTo || "/app/home";
    debug.log("[login-entry] Authenticated — navigating to", destination);
    void navigate(destination, { replace: true });
  }, [isAuthorized, redirectTo, navigate]);

  const handleEntraLogin = () => {
    debug.log("[login-entry] User clicked Microsoft sign-in");
    void instance.loginRedirect({ scopes, prompt: PromptValue.LOGIN });
  };

  if (isAuthorized) {
    const destination = redirectTo || "/app/home";
    return (
      <div className="flex h-full w-full flex-col items-center justify-center space-y-4 text-center">
        <LogoIcon className="mb-2 h-[50px] w-[100px]" />
        <AiOutlineLoading3Quarters className="size-8 animate-spin text-muted-foreground" />
        <h2 className="text-lg font-semibold sm:text-xl">
          {prefixedT("SIGNING_IN")}
        </h2>
        <p className="max-w-xs text-sm leading-relaxed text-muted-foreground sm:max-w-sm sm:text-base">
          {prefixedT("SIGNING_IN_SUBTITLE")}
        </p>
        <Button
          variant="link"
          className="text-xs sm:text-sm"
          onClick={() => void navigate(destination, { replace: true })}
        >
          {destination}
        </Button>
      </div>
    );
  }

  if (isUsingB2CAuth) {
    if (inProgress !== InteractionStatus.None) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center space-y-4 text-center">
          <LogoIcon className="mb-2 h-[50px] w-[100px]" />
          <AiOutlineLoading3Quarters className="size-8 animate-spin text-muted-foreground" />
          <h2 className="text-lg font-semibold sm:text-xl">
            {prefixedT("SIGNING_IN")}
          </h2>
          <p className="max-w-xs text-sm leading-relaxed text-muted-foreground sm:max-w-sm sm:text-base">
            {prefixedT("SIGNING_IN_SUBTITLE")}
          </p>
        </div>
      );
    }

    return (
      <div className="flex h-full w-full flex-col items-center justify-center space-y-8 text-center">
        <LogoIcon className="h-16 w-auto" />
        <Button
          type="button"
          variant="dark"
          size="lg"
          className="w-72"
          onClick={handleEntraLogin}
        >
          {prefixedT("ENTRA_TITLE")}
        </Button>
      </div>
    );
  }

  return <LoginContainer />;
};
